import { useMemo } from "react";
import { toggleSort as sharedToggleSort, toNumber } from "@/shared/utils";
import { PURCHASE_STATUS_ORDER } from "@/features/accounting/shared";
import type { SortConfig } from "@/shared/utils";
import type { PurchaseListItem } from "../types";
import { transactionStatusToPurchaseStatus } from "../types/api";

export type SortKey = "invoice_number" | "status" | "date" | "vendor" | "amount" | "items_count" | "sf_number" | "sf_date";

export type { SortConfig };

export function usePurchasesSort(
  purchases: PurchaseListItem[],
  sortConfig: SortConfig<SortKey> | null,
): PurchaseListItem[] {
  return useMemo(() => {
    if (!sortConfig) {
      return purchases;
    }

    const { key, direction } = sortConfig;

    return [...purchases].sort((a, b) => {
      let cmp = 0;

      if (key === "invoice_number") {
        cmp = a.invoice_number.localeCompare(b.invoice_number);
      } else if (key === "status") {
        const aStatus = transactionStatusToPurchaseStatus(a.transaction_status);
        const bStatus = transactionStatusToPurchaseStatus(b.transaction_status);
        const aStatusOrder = PURCHASE_STATUS_ORDER[aStatus] ?? -1;
        const bStatusOrder = PURCHASE_STATUS_ORDER[bStatus] ?? -1;
        cmp = aStatusOrder - bStatusOrder;
      } else if (key === "date") {
        const aDate = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
        const bDate = b.invoice_date ? new Date(b.invoice_date).getTime() : 0;
        cmp = aDate - bDate;
      } else if (key === "vendor") {
        cmp = a.vendor_name.localeCompare(b.vendor_name);
      } else if (key === "amount") {
        cmp = toNumber(a.total_amount) - toNumber(b.total_amount);
      } else if (key === "items_count") {
        cmp = a.line_items_count - b.line_items_count;
      } else if (key === "sf_number") {
        const aSfNumber = a.sf_number || "";
        const bSfNumber = b.sf_number || "";
        cmp = aSfNumber.localeCompare(bSfNumber);
      } else if (key === "sf_date") {
        const aDate = a.sf_date ? new Date(a.sf_date).getTime() : 0;
        const bDate = b.sf_date ? new Date(b.sf_date).getTime() : 0;
        cmp = aDate - bDate;
      }

      return direction === "asc" ? cmp : -cmp;
    });
  }, [purchases, sortConfig]);
}

export function toggleSort(currentSort: SortConfig<SortKey> | null, newKey: SortKey): SortConfig<SortKey> {
  return sharedToggleSort(currentSort, newKey);
}
